import { FC, useContext, useRef } from "react";

import { FormType, PaymentMethod, TicketingFormCategory } from "@simplyk/common";
import { UseFormReturn } from "react-hook-form";

import { FrontendFormContext } from "../../../../../../common/contexts/FrontendFormContext";
import { usePaymentErrorContext } from "../../../blocks/payment/PaymentSection/PaymentElement/PaymentErrorContext";
import { PayPalDirectButton } from "../../../blocks/payment/PaymentSection/PaymentElement/PayPalDirectButton/PayPalDirectButton";
import { usePaymentSubmit } from "../../../blocks/payment/PaymentSection/PaymentElement/usePaymentSubmit";
import { usePaypal } from "../../../blocks/payment/PaymentSection/PaymentElement/usePaypal";
import { useStripePaymentReadiness } from "../../../blocks/payment/PaymentSection/PaymentElement/useStripePaymentReadiness";
import { useFormV2Context, useTicketingFormV2ContextOrDefaults } from "../../../contexts/FormV2Context";

import { useEnterKeyListener } from "@/canvas/features/FormV2/hooks/useEnterKeyListener";
import { useCardPaymentFormValid } from "@/canvas/features/FormV2/steps/step2/hooks/useCardPaymentFormValid";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DonationFormPaymentInput } from "@/common/types/donationForm";
import { TicketingPaymentInput } from "@/common/types/ticketing";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";
import { HeartsFormButton } from "@/forms-design-system/HeartsFormButton/HeartsFormButton";
import { Lock } from "@/icons/outlined";

export const SubmitCommandButton: FC = () => {
  const { t } = useTranslate();
  const { formObject, form, currentStep } = useFormV2Context();

  const { validDiscount } = useTicketingFormV2ContextOrDefaults();
  const {
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    showProceedButton,
    isBankConnectionPending,
    isSubmitting,
    isPadLoading,
    setIsSubmitting,
  } = useContext(FrontendFormContext);
  const { isPreview } = usePreviewContext();
  const { handlePaymentError, showBankConnectionGuidance } = usePaymentErrorContext();
  const hasGuidedBankConnectionRef = useRef(false);
  const ensureStripeReady = useStripePaymentReadiness(selectedPaymentMethod, handlePaymentError);

  const { handleSubmit, formState, setValue } = form as UseFormReturn<TicketingPaymentInput | DonationFormPaymentInput>;
  const { handleFormSubmit, prepareSubmit, handlePostSubmit } = usePaymentSubmit({
    handleSubmit,
    formObject,
    validDiscount,
    setValue,
  });

  const { createPaypalOrder, capturePaypal } = usePaypal({
    setValue,
    handlePaymentError,
    handlePostSubmit,
    setIsSubmitting,
    prepareSubmit,
  });

  const { isCardPaymentFormValid } = useCardPaymentFormValid();

  const shouldTriggerHeartsAnimation = Boolean(
    formState.isValid && (selectedPaymentMethod === PaymentMethod.Card ? isCardPaymentFormValid : true)
  );

  const label = useSubmitLabel();

  const isPaymentDisabled = formObject?.organization?.isPaymentDisabled || false;
  const isSubmitDisabled = isPreview || isPaymentDisabled || isSubmitting || isPadLoading;

  const submit = async () => {
    if (isSubmitDisabled) {
      return;
    }
    if (!ensureStripeReady()) {
      return;
    }
    if (!selectedPaymentMethod) {
      handlePaymentError({
        rawErrorCode: "no_payment_method_selected",
        rawErrorMessage: t("common", "pleaseSelectPaymentMethod"),
        step: "form-validation",
        paymentMethod: undefined,
        isExpressCheckout: false,
        isKnownError: true,
      });
      setSelectedPaymentMethod(PaymentMethod.Card);
      return;
    }
    if (isBankConnectionPending && !hasGuidedBankConnectionRef.current) {
      hasGuidedBankConnectionRef.current = true;
      showBankConnectionGuidance();
      return;
    }
    handleFormSubmit()();
  };

  useEnterKeyListener(submit, currentStep === "payment" && !isSubmitDisabled);

  if (selectedPaymentMethod === PaymentMethod.PayPal || selectedPaymentMethod === PaymentMethod.Venmo) {
    return (
      <PayPalDirectButton
        selectedPaymentMethod={selectedPaymentMethod}
        createPaypalOrder={createPaypalOrder}
        capturePaypal={capturePaypal}
      />
    );
  }

  return (
    <HeartsFormButton
      onClick={submit}
      disabled={isSubmitDisabled}
      isLoading={isSubmitting && selectedPaymentMethod === PaymentMethod.Daf}
      endIcon={showProceedButton ? <Lock /> : undefined}
      data-test="form-v2-submit-button"
      animated={shouldTriggerHeartsAnimation}
      loop={true}
    >
      {label}
    </HeartsFormButton>
  );
};

const useSubmitLabel = () => {
  const { showProceedButton, isBankConnectionPending } = useContext(FrontendFormContext);
  const { t } = useTranslate();
  const { type, formCategory, form } = useFormV2Context();

  if (isBankConnectionPending) {
    return t("common", "paymentMethod.button.connectBank");
  }

  switch (type) {
    case FormType.DonationForm:
      return t("common", "Donate");

    case FormType.Ticketing: {
      if (showProceedButton) {
        return t("common", "paymentMethod.button.proceedToPayment");
      }

      if (formCategory === TicketingFormCategory.Auction) {
        const bidCount = form.getValues("ticketsPurchased")?.filter((item) => (item?.bid ?? 0) > 0).length ?? 0;
        return t("common", "paymentMethod.button.placeBid", { count: bidCount });
      }

      const totalTickets =
        form.getValues("ticketsPurchased")?.reduce((total, item) => total + (item?.purchasedNumber || 0), 0) || 0;
      if (totalTickets === 0) {
        return t("ticketing", "donate");
      }

      switch (formCategory) {
        case TicketingFormCategory.Event:
        case TicketingFormCategory.Lottery:
          return t("ticketing", "buyTickets");
        case TicketingFormCategory.Sweepstakes:
          return t("ticketing", "sweepstakes.enter");
        case TicketingFormCategory.MembershipV2:
        case TicketingFormCategory.Shop:
          return t("ticketing", "buyItems");
        default:
          return t("common", "buy");
      }
    }
  }
};
